import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, useTheme } from "@mui/material";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/20/solid";
import api from "./components/login/interceptors";
import { BASE_URL } from "./Constants";
import { ErrorToast } from "./components/modals/ErrorToast";
import useFocusTrap from "./hooks/useFocusTrap";
import { fetchAllPanelists, panelistDisplayName, panelistErrorMessage, panelistIdOf, panelistNameError, panelistNamePayload, panelistsUrl } from "./utils/panelists";
import { validateEmail } from "./components/auth/validationHelpers";
import {
  INTEGRATION_PROVIDER,
  INTEGRATION_STATUS,
  bookableProviderIds,
  describeCalendarError,
  earliestBookableClock,
  earliestBookableDate,
  normalizeInterview,
  scheduleSlotError,
  setInterviewAIMode,
  validationErrorField,
  zonedDateTime,
} from "./utils/interviewApi";
import MeetTypePicker from "./components/modals/MeetTypePicker";
import { AdminConsentOffer, ConnectCalendarDialog, ProviderConnectButtons, useCalendarConnections } from "./components/modals/MeetingIntegrations";
import { fetchGoogleStatus, GOOGLE_STATUS } from "./utils/interviewApi";
import useAIInterviewSetting from "./hooks/useAIInterviewSetting";
import AIInterviewerChoice from "./components/aiInterview/AIInterviewerChoice";
import {
  AI_MIN_LEAD_MINUTES,
  PANELIST_OPTIONAL_HINT,
  PANELIST_REQUIRED,
  meetingOfInterview,
  isAIChoice,
  meetingProviderOf,
  panelistsRequired,
} from "./utils/aiInterviewerChoice";
import AICreditCost from "./components/aiInterview/AICreditCost";
import { creditSummary, refreshPlan, usePlan } from "./utils/subscriptionData";
import usePlanGate from "./hooks/usePlanGate";
import { announcePlanBlocked } from "./utils/planEvents";

const asList = (data, keys) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    for (const key of keys) {
      if (Array.isArray(data[key])) return data[key];
    }
  }
  return [];
};

const candidateIdOf = (candidate) => String(candidate.candidate_id || candidate.lead_id || "");

const displayName = (candidate) =>
  candidate.name ||
  `${candidate.first_name || ""} ${candidate.last_name || ""}`.trim() ||
  "Unnamed candidate";

const padTime = (value) => String(value).padStart(2, "0");

const SLOT_STEP_MINUTES = 15;
const DURATION_CHOICES = [15, 30, 45, 60, 90, 120];
const DEFAULT_DURATION = 30;

const clockMinutes = (time) => {
  const match = /^(\d{2}):(\d{2})/.exec(time || "");
  return match ? Number(match[1]) * 60 + Number(match[2]) : NaN;
};

const toClock = (minutes) => `${padTime(Math.floor(minutes / 60))}:${padTime(minutes % 60)}`;

// Wall-clock arithmetic: the date and time `minutes` after `date` + `time`
const addMinutes = (date, time, minutes) => {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec((date || "").slice(0, 10));
  const start = clockMinutes(time);
  if (!day || Number.isNaN(start)) return { date: "", time: "" };
  const moved = new Date(Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3]), 0, start + minutes));
  return {
    date: moved.toISOString().slice(0, 10),
    time: `${padTime(moved.getUTCHours())}:${padTime(moved.getUTCMinutes())}`,
  };
};

const clockLabel = (time) => {
  const minutes = clockMinutes(time);
  if (Number.isNaN(minutes)) return time || "";
  const hours = Math.floor(minutes / 60);
  return `${hours % 12 || 12}:${padTime(minutes % 60)} ${hours < 12 ? "AM" : "PM"}`;
};

const dateLabel = (date) => {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date || "";
  return parsed.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
};

const durationLabel = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return [hours && `${hours} hr`, rest && `${rest} min`].filter(Boolean).join(" ");
};

// The next quarter hour that leaves the AI its lead time, so any interviewer can take it
const defaultStart = (timeZone) => {
  const earliest = clockMinutes(earliestBookableClock(timeZone)) + AI_MIN_LEAD_MINUTES;
  const rounded = Math.ceil(earliest / SLOT_STEP_MINUTES) * SLOT_STEP_MINUTES;
  return addMinutes(earliestBookableDate(timeZone), "00:00", rounded);
};

// Start times on the quarter hour, from now on when the date is today
const startTimeChoices = (date, timeZone, current) => {
  const earliest = date === earliestBookableDate(timeZone) ? clockMinutes(earliestBookableClock(timeZone)) : 0;
  const times = [];
  for (let minutes = Math.ceil(earliest / SLOT_STEP_MINUTES) * SLOT_STEP_MINUTES; minutes < 24 * 60; minutes += SLOT_STEP_MINUTES) {
    times.push(toClock(minutes));
  }
  if (current && !times.includes(current)) times.push(current);
  return times.sort().map((time) => ({ value: time, label: clockLabel(time) }));
};

const durationChoices = (current) =>
  [...new Set([...DURATION_CHOICES, current].filter(Boolean))]
    .sort((a, b) => a - b)
    .map((minutes) => ({ value: minutes, label: durationLabel(minutes) }));

const zoneParts = (iso, timeZone) => {
  if (!iso) return { date: "", time: "" };
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return { date: "", time: "" };
  let zone = timeZone;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: zone });
  } catch {
    zone = undefined;
  }
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(parsed).map((part) => [part.type, part.value])
  );
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}` };
};

const rowTimeError = (row, timeZone) =>
  scheduleSlotError(row.date, row.startTime, row.endTime, timeZone, row.endDate);

// "Sat 24 Oct · 11:45 PM – 12:30 PM", or with the end day when it runs past midnight
const slotLabel = (row) => {
  const end = row.endDate && row.endDate !== row.date ? `${dateLabel(row.endDate)} · ${clockLabel(row.endTime)}` : clockLabel(row.endTime);
  return `${dateLabel(row.date)} · ${clockLabel(row.startTime)} – ${end}`;
};

const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "";

const timezoneChoices = (current) => {
  let zones = [];
  try {
    zones = Intl.supportedValuesOf("timeZone");
  } catch {
    zones = [];
  }
  const value = (current || "").trim();
  if (value && !zones.includes(value)) zones = [value, ...zones];
  if (!zones.length && value) zones = [value];
  return zones.map((zone) => ({ value: zone, label: zone }));
};

const apiMessage = (err, fallback) => {
  const data = err?.response?.data;
  const details = Array.isArray(data?.errors)
    ? data.errors.map((item) => item?.error || item?.message).filter(Boolean).join(" ")
    : "";
  return details || data?.error || data?.message || fallback;
};

const roundsUrl = `${BASE_URL}/api/interview-rounds`;

const roundsFromResponse = (data) => (Array.isArray(data?.rounds) ? data.rounds : []);

const roundErrorMessage = (err, fallback) => {
  const data = err?.response?.data || {};
  const code = data.code || data.error;
  if (code === "ROUND_NAME_EXISTS") return "A round with this name already exists.";
  if (code === "ROUND_READ_ONLY" || err?.response?.status === 403) return "Default rounds can't be changed.";
  return data.message || (typeof data.error === "string" ? data.error : "") || fallback;
};

const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?";

const FormDropdown = ({ id, value, onChange, placeholder, options, disabled, action, multiple, searchable = false, addLabel = "" }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const selectedValues = multiple ? (Array.isArray(value) ? value : []) : null;
  const selected = multiple
    ? options.filter((option) => selectedValues.includes(option.value))
    : options.find((option) => option.value === value);
  const closedLabel = (option) => option.valueLabel || option.label;
  const selectedLabel = multiple
    ? selected.map(closedLabel).join(", ")
    : closedLabel(selected || {});
  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions = searchable && normalizedQuery
    ? options.filter((option) => (option.label || "").toLowerCase().includes(normalizedQuery))
    : options;

  const placeMenu = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < 240 && rect.top > spaceBelow;
    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      zIndex: 80,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  };

  useEffect(() => {
    if (open) return undefined;
    setQuery("");
    return undefined;
  }, [open]);

  // Open on the selected option, so a long list like start times doesn't start at the top
  const scrolledRef = useRef(false);
  useEffect(() => {
    if (!open) {
      scrolledRef.current = false;
      return;
    }
    const menu = menuRef.current;
    if (scrolledRef.current || !menu) return;
    scrolledRef.current = true;
    const item = menu.querySelector('[aria-selected="true"]');
    if (item) menu.scrollTop = item.offsetTop - (menu.clientHeight - item.offsetHeight) / 2;
  }, [open, menuStyle]);

  useEffect(() => {
    if (!open) return undefined;
    if (searchable) searchRef.current?.focus();
    placeMenu();
    const handlePointerDown = (event) => {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
    };
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [open]);

  const borderColor = theme.palette.secondary.v4;
  const hasBadges = multiple && selected.length > 0;
  const removeSelected = (optionValue) => {
    onChange(selectedValues.filter((item) => item !== optionValue));
  };

  return (
    <div>
      <div
        id={id}
        ref={buttonRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!disabled) setOpen((current) => !current);
        }}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          if (!disabled) setOpen((current) => !current);
        }}
        className={`w-full px-3 ${hasBadges ? "py-2" : "py-2.5"} border rounded-lg bg-white text-left flex items-center gap-2 ${disabled ? "opacity-60" : ""}`}
        style={{ borderColor, color: "#111111" }}
      >
        {hasBadges ? (
          <span className="flex min-w-0 flex-1 flex-nowrap gap-1.5 overflow-x-auto">
            {selected.map((option) => {
              const name = closedLabel(option);
              return (
                <span
                  key={option.value}
                  className="inline-flex shrink-0 items-center gap-2 rounded-full border bg-[#f6f8fb] py-1 pl-1 pr-2.5 text-sm font-medium"
                  style={{ borderColor }}
                >
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded-full bg-[#dbe6f7] text-[10px] font-semibold"
                    style={{ color: theme.palette.info.main }}
                    aria-hidden="true"
                  >
                    {initialsOf(name)}
                  </span>
                  <span className="whitespace-nowrap">{name}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${name}`}
                    disabled={disabled}
                    className="inline-flex shrink-0 text-gray-500 hover:text-black"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!disabled) removeSelected(option.value);
                    }}
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </span>
              );
            })}
            {addLabel && <span className="shrink-0 self-center pl-1 text-sm text-gray-500">{addLabel}</span>}
          </span>
        ) : (
          <span className={`min-w-0 flex-1 truncate ${selectedLabel ? "" : "text-gray-500"}`}>
            {selectedLabel || placeholder}
          </span>
        )}
        {hasBadges && (
          <button
            type="button"
            aria-label="Clear selected"
            disabled={disabled}
            className="inline-flex shrink-0 text-gray-500"
            onClick={(event) => {
              event.stopPropagation();
              if (!disabled) onChange([]);
            }}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
        <ChevronDownIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
      </div>
      {open && menuStyle && createPortal(
        <ul
          ref={menuRef}
          role="listbox"
          aria-labelledby={id}
          aria-multiselectable={multiple || undefined}
          className="max-h-60 overflow-auto rounded-md border bg-white shadow-lg"
          style={{ ...menuStyle, borderColor }}
        >
          {searchable && (
            <li className="sticky top-0 bg-white pb-1">
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search"
                aria-label="Search"
                className="w-full border-b px-2 py-1.5 text-sm"
                style={{ borderColor, color: "#111111" }}
              />
            </li>
          )}
          {visibleOptions.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500">{options.length === 0 ? "No options" : "No matches"}</li>
          )}
          {visibleOptions.map((option) => {
            const isSelected = multiple
              ? selectedValues.includes(option.value)
              : option.value === value;
            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                  style={{
                    color: "#111111",
                    backgroundColor: isSelected ? "#E8EEF8" : "#ffffff",
                  }}
                  onClick={() => {
                    if (multiple) {
                      onChange(
                        isSelected
                          ? selectedValues.filter((item) => item !== option.value)
                          : [...selectedValues, option.value]
                      );
                      return;
                    }
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {multiple && (
                    <input
                      type="checkbox"
                      readOnly
                      checked={isSelected}
                      tabIndex={-1}
                      aria-hidden="true"
                      className="pointer-events-none"
                    />
                  )}
                  <span>{option.label}</span>
                </button>
              </li>
            );
          })}
          {action && (
            <li className="border-t" style={{ borderColor }}>
              <button
                type="button"
                className={`w-full text-left px-3 py-2 text-sm font-medium ${action.unavailable ? "cursor-not-allowed" : "hover:bg-gray-100"}`}
                style={{ color: theme.palette.info.main, opacity: action.unavailable ? 0.55 : 1 }}
                aria-disabled={action.unavailable || undefined}
                title={action.unavailable || undefined}
                onClick={() => {
                  action.onClick();
                  setOpen(false);
                }}
              >
                {action.label}
                {action.unavailable && <span className="block text-xs font-normal text-gray-600">{action.unavailable}</span>}
              </button>
            </li>
          )}
        </ul>,
        document.body
      )}
    </div>
  );
};

// Creates interviews for a job's candidates, or schedules/reschedules `interview` (edit form).
// From a candidate's page, `candidate` ({ candidate_id, name, email }) is the only candidate and the job is
// picked from `candidateJobs` ([{ id, title }], id = jobdesc id). A job with one of `pendingInterviews`
// (normalized, with jdId and roundId) books that interview instead of creating another.
// `onScheduled` gets the saved interviews, normalized, when they were created or booked here.
const ScheduleInterviewModal = ({
  isOpen,
  onClose,
  onScheduled,
  interview,
  defaultJdUuid = "",
  candidate: fixedCandidate = null,
  candidateJobs = [],
  defaultJobId = "",
  pendingInterviews = [],
}) => {
  const theme = useTheme();
  // Escape goes through handleClose so a save that's waiting on the AI retry still refreshes the list
  const closeRef = useRef(onClose);
  const modalRef = useFocusTrap(isOpen, () => closeRef.current());
  const [interviewers, setInterviewers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [calendarProvider, setCalendarProvider] = useState(INTEGRATION_PROVIDER.GOOGLE);
  const [hideMicrosoft, setHideMicrosoft] = useState(false);
  const [hideGoogle, setHideGoogle] = useState(false);
  const [offerAdminConsent, setOfferAdminConsent] = useState(false);
  const [connectPrompt, setConnectPrompt] = useState([]);
  const { connections, loading: connectionsLoading, error: connectionsError, refresh: refreshConnections } = useCalendarConnections(isOpen);
  // The Calendar & Meetings dialog, opened from the prompt below when nothing can be booked yet
  const [connectOpen, setConnectOpen] = useState(false);
  const [panelistIds, setPanelistIds] = useState([]);
  const [timezone, setTimezone] = useState(browserTimezone);
  const [rescheduleReason, setRescheduleReason] = useState("");
  const [selectedJdUuid, setSelectedJdUuid] = useState("");
  const [jobdescId, setJobdescId] = useState("");
  const [candidateCount, setCandidateCount] = useState("1");
  const [topCandidates, setTopCandidates] = useState(true);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [roundId, setRoundId] = useState("");
  const [showAddPanelist, setShowAddPanelist] = useState(false);
  const [newPanelistFirstName, setNewPanelistFirstName] = useState("");
  const [newPanelistLastName, setNewPanelistLastName] = useState("");
  const [newPanelistEmail, setNewPanelistEmail] = useState("");
  const [panelistError, setPanelistError] = useState("");
  const [addingPanelist, setAddingPanelist] = useState(false);
  const [showAddRound, setShowAddRound] = useState(false);
  const [editingRoundId, setEditingRoundId] = useState("");
  const [newRoundName, setNewRoundName] = useState("");
  const [roundError, setRoundError] = useState("");
  const [addingRound, setAddingRound] = useState(false);
  const [pool, setPool] = useState([]);
  // Candidates to schedule. `date` and `startTime` are only used when each candidate gets their own time.
  const [rows, setRows] = useState([]);
  const [slotDate, setSlotDate] = useState("");
  const [slotTime, setSlotTime] = useState("");
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [timeEach, setTimeEach] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  // Server or client errors shown on the Panelists and Interviewer fields
  const [panelistFieldError, setPanelistFieldError] = useState("");
  const [interviewerError, setInterviewerError] = useState("");
  // The booked interview's time and panel when the edit form opened, to spot a mode-only change
  const [original, setOriginal] = useState(null);
  // Interviews saved while the form waits on the AI retry, reported when it closes
  const [savedInterviews, setSavedInterviews] = useState([]);
  const [error, setError] = useState("");

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(""), 4000);
  };

  const requestedCount = Math.max(1, parseInt(candidateCount, 10) || 1);
  const isEdit = Boolean(interview?.interview_id);
  const fixedCandidateId = fixedCandidate ? String(fixedCandidate.candidate_id || "") : "";
  // The candidate's pending interview for the chosen job already has its round; we only book a time for it
  const pendingForJob = fixedCandidate && jobdescId
    ? pendingInterviews.find((item) => item.id && String(item.jdId) === String(jobdescId)) || null
    : null;
  const isReschedule = isEdit && interview.status !== "pending";
  // Each candidate's slot. With one shared time, AI interviews run in parallel and panel
  // interviews run back to back so the panel isn't double-booked.
  const scheduleRows = (parallel) => rows.map((row, index) => {
    const start = timeEach
      ? { date: row.date || slotDate, time: row.startTime || slotTime }
      : addMinutes(slotDate, slotTime, parallel ? 0 : index * duration);
    const end = addMinutes(start.date, start.time, duration);
    return {
      ...row,
      date: start.date,
      startTime: start.time,
      // An interview can run past midnight; the server takes the end day as end_date
      endDate: end.date,
      endTime: end.time,
    };
  });
  const ai = useAIInterviewSetting(isOpen, interview?.interview_id, {
    // The AI checks each slot's length and lead time, which don't depend on parallel vs back to back
    slots: scheduleRows(true).map((row) => ({
      start: zonedDateTime(row.date, row.startTime, timezone),
      end: zonedDateTime(row.endDate, row.endTime, timezone),
    })),
    // A reschedule keeps the interview's meeting; otherwise the chosen calendar creates the link
    meetingProvider: isReschedule
      ? meetingProviderOf(meetingOfInterview(interview))
      : meetingProviderOf({ calendarProvider }),
    // Older payloads without ai_mode fall back to the AI session
    aiMode: isEdit && "ai_mode" in interview ? interview.ai_mode : undefined,
  });
  // Interviews were saved but the AI step failed: the form waits for retry or close
  const awaitingAIRetry = ai.failures.length > 0;
  // The AI interviewer can run the interview alone. With no panel, candidates can share a time.
  const needsPanel = panelistsRequired(ai.choice);
  const parallel = !needsPanel && !panelistIds.length;
  const scheduled = scheduleRows(parallel);
  // Only the Interviewer field changed on a booked interview: /ai-mode, so the candidate isn't sent a reschedule
  const samePanel = Boolean(original)
    && original.panelistIds.length === panelistIds.length
    && panelistIds.every((id) => original.panelistIds.includes(id));
  const slotUnchanged = isReschedule && samePanel
    && original.date === slotDate
    && original.time === slotTime
    && original.duration === duration
    && original.timezone === timezone;
  const aiOnlyChange = slotUnchanged && (ai.modeChanged || Boolean(ai.action));

  // AI credits: one per interview when AI is switched on, so the cost is known before anything is confirmed.
  // Switching between AI modes or rescheduling uses no more; switching AI off gives the credit back.
  const { data: planData } = usePlan();
  // Adding a panelist from here uses a panelist seat
  const panelistGate = usePlanGate("panelist_seats");
  const creditKind = ai.action === "enable" ? "use" : ai.action === "disable" ? "refund" : isAIChoice(ai.choice) ? "already" : "free";
  const creditCount = isEdit || pendingForJob ? 1 : Math.max(1, scheduled.length);
  const credits = creditSummary({
    usage: planData?.usage?.ai_credits,
    count: creditKind === "use" ? creditCount : 0,
    minutes: duration,
    planActive: planData ? Boolean(planData.plan) && planData.plan.active !== false : true,
  });
  // Nothing can be covered: the server would refuse every interview, so say so up front
  const creditsBlocked = creditKind === "use" && credits.none;
  const multiple = !isEdit && rows.length > 1;
  const providerFilter = { hideMicrosoft, hideGoogle };
  const bookable = bookableProviderIds(connections, providerFilter).filter((id) => !hideGoogle || id !== INTEGRATION_PROVIDER.GOOGLE);
  const showPicker = !isReschedule;
  const bookingBlocked = !isReschedule && !connectionsLoading && Boolean(connections) && bookable.length === 0;
  // A calendar that needs signing in again counts too: it can't create meetings until it is reconnected
  const needsReconnect = Object.values(connections || {}).some((item) => item?.configured !== false && item?.status === INTEGRATION_STATUS.REAUTH_REQUIRED);
  const connectPromptText = bookingBlocked
    ? needsReconnect
      ? "Your calendar needs to be reconnected before you can schedule an interview."
      : "Connect your calendar and meeting account before you can schedule an interview."
    : "";
  const selectedRound = rounds.find((item) => String(item.round_id) === String(roundId));
  const schedulablePool = pool.filter((candidate) => candidate.schedulable !== false);
  const unschedulableCount = pool.length - schedulablePool.length;
  // The job has nobody who can be scheduled; the message under the job explains why
  const nothingToSchedule = !isEdit && Boolean(selectedJdUuid) && !loadingCandidates && schedulablePool.length === 0;
  const fewerNote =
    topCandidates && selectedJdUuid && schedulablePool.length > 0 && schedulablePool.length < requestedCount
      ? `This job has ${schedulablePool.length} candidate${schedulablePool.length === 1 ? "" : "s"} who can be scheduled. Showing all of them.`
      : "";
  const unschedulableNote =
    selectedJdUuid && unschedulableCount > 0
      ? `${unschedulableCount} candidate${unschedulableCount === 1 ? "" : "s"} ${unschedulableCount === 1 ? "has" : "have"} no email and can't be scheduled until an email is added.`
      : "";

  const resetForm = () => {
    setCalendarProvider(INTEGRATION_PROVIDER.GOOGLE);
    setHideMicrosoft(false);
    setHideGoogle(false);
    setOfferAdminConsent(false);
    setConnectPrompt([]);
    setPanelistIds([]);
    setTimezone(browserTimezone());
    setRescheduleReason("");
    setSelectedJdUuid("");
    setJobdescId("");
    setCandidateCount("1");
    setTopCandidates(true);
    setSelectedCandidateIds([]);
    setRoundId("");
    setShowAddPanelist(false);
    setNewPanelistFirstName("");
    setNewPanelistLastName("");
    setNewPanelistEmail("");
    setPanelistError("");
    setShowAddRound(false);
    setEditingRoundId("");
    setNewRoundName("");
    setRoundError("");
    setPool([]);
    setRows([]);
    setSlotDate("");
    setSlotTime("");
    setDuration(DEFAULT_DURATION);
    setTimeEach(false);
    setFormError("");
    setPanelistFieldError("");
    setInterviewerError("");
    setOriginal(null);
    setSavedInterviews([]);
    setError("");
  };

  const handleClose = () => {
    const saved = awaitingAIRetry;
    resetForm();
    if (saved) onScheduled(savedInterviews);
    onClose();
  };
  closeRef.current = handleClose;

  // The balance shown beside the duration should be current when the form opens
  useEffect(() => {
    if (!isOpen) return;
    try {
      refreshPlan().catch(() => {});
    } catch {
      // The form works without the balance; the server decides
    }
  }, [isOpen]);

  const finishSave = (saved = savedInterviews) => {
    resetForm();
    onScheduled(saved);
    onClose();
  };

  const handleRetryAI = async () => {
    if (await ai.retry()) finishSave();
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!interview) {
      setCalendarProvider(INTEGRATION_PROVIDER.GOOGLE);
      setHideMicrosoft(false);
      setHideGoogle(false);
      setOfferAdminConsent(false);
      setConnectPrompt([]);
      setPanelistIds([]);
      setTimezone(browserTimezone());
      setRescheduleReason("");
      setSelectedJdUuid(fixedCandidate ? "" : defaultJdUuid || "");
      setJobdescId(fixedCandidate ? String(defaultJobId || "") : "");
      setSavedInterviews([]);
      setCandidateCount("1");
      setTopCandidates(true);
      setSelectedCandidateIds([]);
      setRoundId("");
      setShowAddPanelist(false);
      setNewPanelistFirstName("");
      setNewPanelistLastName("");
      setNewPanelistEmail("");
      setPanelistError("");
      setShowAddRound(false);
      setEditingRoundId("");
      setNewRoundName("");
      setRoundError("");
      setPool([]);
      setRows([]);
      const start = defaultStart(browserTimezone());
      setSlotDate(start.date);
      setSlotTime(start.time);
      setDuration(DEFAULT_DURATION);
      setTimeEach(false);
      setFormError("");
      setPanelistFieldError("");
      setInterviewerError("");
      setOriginal(null);
      setError("");
      return;
    }
    setCalendarProvider(INTEGRATION_PROVIDER.GOOGLE);
    const panel = (interview.panelists || []).map((person) => String(person.panelist_id)).filter(Boolean);
    setPanelistIds(panel);
    setPanelistFieldError("");
    setInterviewerError("");
    const zone = interview.timezone || browserTimezone();
    setTimezone(zone);
    setRescheduleReason("");
    // A pending interview has no time yet
    const when = interview.start_utc ? zoneParts(interview.start_utc, zone) : defaultStart(zone);
    const minutes = interview.duration_minutes
      || (interview.start_utc && interview.end_utc
        ? Math.round((new Date(interview.end_utc) - new Date(interview.start_utc)) / 60000)
        : 0);
    setSlotDate(when.date);
    setSlotTime(when.time);
    setDuration(minutes > 0 ? minutes : DEFAULT_DURATION);
    setTimeEach(false);
    setOriginal(interview.start_utc
      ? { date: when.date, time: when.time, duration: minutes > 0 ? minutes : DEFAULT_DURATION, timezone: zone, panelistIds: panel }
      : null);
    const count = 1;
    setCandidateCount(String(Math.max(1, count)));
    if (interview.round_id) setRoundId(String(interview.round_id));
    // fixedCandidate is read only to pick the job; its id is enough to tell a different candidate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, interview, defaultJdUuid, defaultJobId, fixedCandidateId]);

  useEffect(() => {
    if (pendingForJob) setRoundId(String(pendingForJob.roundId || ""));
  }, [pendingForJob]);

  // The last save's error no longer applies once anything on the form changes
  useEffect(() => {
    setFormError("");
  }, [
    slotDate, slotTime, duration, timezone, timeEach, rows, panelistIds, ai.choice, calendarProvider,
    selectedJdUuid, jobdescId, roundId, topCandidates, candidateCount, selectedCandidateIds, rescheduleReason,
  ]);

  useEffect(() => {
    if (!isOpen || !interview || interview.interview_id || interview.jd_uuid || selectedJdUuid) return;
    const match = jobs.find(
      (job) => job.jobdesc_id === interview.jobdesc_id || job.jd_id === interview.jobdesc_id
    );
    if (match?.jd_uuid) setSelectedJdUuid(match.jd_uuid);
  }, [isOpen, interview, jobs, selectedJdUuid]);

  useEffect(() => {
    if (!isOpen || !interview || roundId) return;
    const match = rounds.find(
      (item) =>
        String(item.round_id) === String(interview.round_id) ||
        (interview.round && (item.name || "").toLowerCase() === String(interview.round).toLowerCase())
    );
    if (match?.round_id) setRoundId(String(match.round_id));
  }, [isOpen, interview, rounds, roundId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [panelists, jobRes, roundRes] = await Promise.all([
          fetchAllPanelists().catch(() => []),
          api.get(`${BASE_URL}/api/job-descriptions/`).catch(() => ({ data: [] })),
          api.get(roundsUrl).catch(() => ({ data: { rounds: [] } })),
        ]);
        if (cancelled) return;
        const jobList = asList(jobRes.data, ["job_descriptions", "data", "results"]);
        const roundList = roundsFromResponse(roundRes.data);
        setInterviewers(panelists);
        setJobs(jobList);
        setRounds(roundList);
      } catch (err) {
        console.error("Error loading schedule options:", err);
        if (!cancelled) showError(apiMessage(err, "Could not load panelists or jobs."));
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    let cancelled = false;

    const loadCandidates = async () => {
      // A candidate's page schedules that candidate only; the job id comes from candidateJobs
      if (fixedCandidateId) return;
      if (!isOpen || !selectedJdUuid) {
        setPool([]);
        setJobdescId("");
        setLoadingCandidates(false);
        return;
      }

      setLoadingCandidates(true);
      setFormError("");
      try {
        const job = jobs.find((item) => item.jd_uuid === selectedJdUuid);
        let resolvedId = job?.jobdesc_id || job?.jd_id;
        if (!resolvedId) {
          const jobResponse = await api.get(`${BASE_URL}/api/job-desc/${selectedJdUuid}`);
          const jobData = Array.isArray(jobResponse.data) ? jobResponse.data[0] : jobResponse.data;
          resolvedId = jobData?.jobdesc_id;
        }
        if (!resolvedId) {
          throw new Error("This job is missing an id.");
        }
        const candidateResponse = await api.get(`${BASE_URL}/api/jd/${resolvedId}/candidates`);
        const candidates = asList(candidateResponse.data, ["candidates", "data", "results"]).map((candidate) => ({
          ...candidate,
          candidate_id: candidateIdOf(candidate),
          email: candidate.email || "",
          schedulable: typeof candidate.schedulable === "boolean" ? candidate.schedulable : Boolean(candidate.email),
        }));
        if (cancelled) return;
        setJobdescId(resolvedId);
        setPool(candidates);
      } catch (err) {
        console.error("Error loading candidates for interview:", err);
        if (!cancelled) {
          setPool([]);
          setJobdescId("");
          showError(apiMessage(err, "Could not load candidates for this job."));
        }
      } finally {
        if (!cancelled) setLoadingCandidates(false);
      }
    };

    loadCandidates();
    return () => {
      cancelled = true;
    };
    // Reload only when the selected job changes. `jobs` is read for its id and
    // must not restart this request, or the warning flashes on every refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJdUuid, isOpen]);

  useEffect(() => {
    if (fixedCandidateId && !interview?.interview_id) {
      setRows([{ candidate_id: fixedCandidateId, name: fixedCandidate.name || "Candidate", email: fixedCandidate.email || "" }]);
      return;
    }
    if (interview?.interview_id && interview.candidate) {
      setRows([{
        candidate_id: String(interview.candidate.candidate_id || ""),
        name: interview.candidate.name || displayName(interview.candidate),
        email: interview.candidate.email || "",
      }]);
      return;
    }
    setRows((previous) => {
      const available = pool.filter((candidate) => candidate.schedulable !== false);
      if (!available.length) return [];
      const picked = topCandidates
        ? available
        : selectedCandidateIds
          .map((id) => available.find((candidate) => candidateIdOf(candidate) === id))
          .filter(Boolean);
      const limit = topCandidates ? requestedCount : picked.length;
      const next = [];
      const used = new Set();

      const take = (candidate) => {
        if (next.length >= limit) return;
        const id = candidateIdOf(candidate);
        if (!id || used.has(id)) return;
        used.add(id);
        const existing = previous.find((row) => row.candidate_id === id);
        next.push({
          candidate_id: id,
          name: displayName(candidate),
          email: candidate.email || "",
          date: existing?.date || "",
          startTime: existing?.startTime || "",
        });
      };

      if (topCandidates) {
        previous.forEach((row) => {
          const candidate = available.find((item) => candidateIdOf(item) === row.candidate_id);
          if (candidate) take(candidate);
        });
      }
      picked.forEach((candidate) => take(candidate));
      return next;
    });
    // fixedCandidate's fields are read with its id; the object itself changes on every parent render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool, requestedCount, interview, topCandidates, selectedCandidateIds, fixedCandidateId]);

  const updateRow = (index, patch) => {
    setRows((previous) => previous.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  // Each candidate starts from the time they had with the shared time
  const toggleTimeEach = () => {
    if (!timeEach) {
      setRows(scheduled.map(({ candidate_id, name, email, date, startTime }) => ({ candidate_id, name, email, date, startTime })));
    }
    setTimeEach((current) => !current);
  };

  // Leaving "AI interviewer" with no panel flags the Panelists field instead of clearing anything
  const handleInterviewerChange = (next) => {
    // An AI option that would use a credit when there is none: explain how to get credits instead of selecting it
    if (isAIChoice(next) && ai.wouldEnable(next)) {
      const available = creditSummary({
        usage: planData?.usage?.ai_credits,
        count: 1,
        minutes: duration,
        planActive: planData ? Boolean(planData.plan) && planData.plan.active !== false : true,
      });
      if (available.none) {
        announcePlanBlocked(
          available.inactive
            ? { code: "NO_ACTIVE_PLAN" }
            : {
                code: "PLAN_LIMIT_REACHED",
                metric: "ai_credits",
                message: "You don't have enough AI credits for this interview. Upgrade your plan or add credits to continue.",
              }
        );
        return;
      }
    }
    ai.setChoice(next);
    setInterviewerError("");
    if (!panelistsRequired(next)) setPanelistFieldError("");
    else if (!panelistIds.length && !panelistsRequired(ai.choice)) setPanelistFieldError(PANELIST_REQUIRED);
  };

  const closePanelistForm = () => {
    setShowAddPanelist(false);
    setNewPanelistFirstName("");
    setNewPanelistLastName("");
    setNewPanelistEmail("");
    setPanelistError("");
  };

  const handleCreatePanelist = async () => {
    const firstName = newPanelistFirstName.trim();
    const lastName = newPanelistLastName.trim();
    const email = newPanelistEmail.trim();
    const nameError = panelistNameError({ firstName, lastName });
    if (nameError) {
      setPanelistError(nameError);
      return;
    }
    const emailError = validateEmail(email);
    if (emailError) {
      setPanelistError(emailError);
      return;
    }
    if (interviewers.some((person) => (person.email || "").trim().toLowerCase() === email.toLowerCase())) {
      setPanelistError("A panelist with this email already exists.");
      return;
    }

    setAddingPanelist(true);
    setPanelistError("");
    try {
      const response = await api.post(panelistsUrl, {
        ...panelistNamePayload({ name: firstName, lastName }),
        email,
      });
      const created = response.data?.panelist || response.data?.interviewer || response.data || {};
      let person = {
        ...created,
        name: created.name || firstName,
        last_name: created.last_name ?? lastName,
        email: created.email || email,
        panelist_id: panelistIdOf(created) || created.id || created._id,
      };
      if (!panelistIdOf(person)) {
        const refreshed = await fetchAllPanelists();
        setInterviewers(refreshed);
        person = refreshed.find((item) => (item.email || "").trim().toLowerCase() === email.toLowerCase()) || person;
      } else {
        setInterviewers((prev) => [...prev, person]);
      }
      const createdId = String(panelistIdOf(person) || "");
      if (createdId) {
        setPanelistIds((prev) => (prev.includes(createdId) ? prev : [...prev, createdId]));
      }
      closePanelistForm();
    } catch (err) {
      setPanelistError(panelistErrorMessage(err, "Could not add panelist."));
    } finally {
      setAddingPanelist(false);
    }
  };

  const closeRoundForm = () => {
    setShowAddRound(false);
    setEditingRoundId("");
    setNewRoundName("");
    setRoundError("");
  };

  const refreshRounds = async () => {
    const listResponse = await api.get(roundsUrl);
    const nextRounds = roundsFromResponse(listResponse.data);
    setRounds(nextRounds);
    return nextRounds;
  };

  const handleSaveRound = async () => {
    const name = newRoundName.trim();
    if (!name) {
      setRoundError("Round name is required");
      return;
    }
    const editing = rounds.find((item) => String(item.round_id) === String(editingRoundId));
    if (editing?.is_default) {
      setRoundError("Default rounds can't be changed.");
      return;
    }

    setAddingRound(true);
    setRoundError("");
    try {
      const response = editingRoundId
        ? await api.patch(`${roundsUrl}/${editingRoundId}`, { name })
        : await api.post(roundsUrl, { name });
      const created = response.data?.round || {};
      const nextRounds = await refreshRounds().catch(() => null);
      const resolvedRounds = nextRounds && nextRounds.length
        ? nextRounds
        : [
            ...rounds.filter((item) => String(item.round_id) !== String(created.round_id || editingRoundId)),
            { round_id: created.round_id || editingRoundId, name: created.name || name, is_default: false },
          ];
      if (!nextRounds) setRounds(resolvedRounds);
      const match =
        resolvedRounds.find((item) => created.round_id && String(item.round_id) === String(created.round_id)) ||
        resolvedRounds.find((item) => String(item.round_id) === String(editingRoundId)) ||
        resolvedRounds.find((item) => (item.name || "").toLowerCase() === name.toLowerCase());
      if (match?.round_id) setRoundId(String(match.round_id));
      closeRoundForm();
    } catch (err) {
      console.error("Error saving round:", err);
      setRoundError(roundErrorMessage(err, editingRoundId ? "Could not update round." : "Could not add round."));
    } finally {
      setAddingRound(false);
    }
  };

  const handleDeleteRound = async () => {
    if (!selectedRound || selectedRound.is_default) {
      setRoundError("Default rounds can't be changed.");
      return;
    }
    const confirmed = window.confirm(`Delete ${selectedRound.name}?`);
    if (!confirmed) return;
    setRoundError("");
    try {
      await api.delete(`${roundsUrl}/${selectedRound.round_id}`);
      setRounds((previous) => previous.filter((item) => String(item.round_id) !== String(selectedRound.round_id)));
      setRoundId("");
      closeRoundForm();
    } catch (err) {
      console.error("Error deleting round:", err);
      setRoundError(roundErrorMessage(err, "Could not delete round."));
    }
  };

  const slotFor = (row) => ({
    date: (row.date || "").slice(0, 10),
    start_time: (row.startTime || "").slice(0, 5),
    end_time: (row.endTime || "").slice(0, 5),
    ...(row.endDate && row.endDate !== row.date ? { end_date: row.endDate } : {}),
  });

  // That message is the only error to show, so drop any left from an earlier attempt
  useEffect(() => {
    if (nothingToSchedule) setFormError("");
  }, [nothingToSchedule]);

  const formValidationError = () => {
    if (!timezone.trim()) return "Timezone is required.";
    const timeError = scheduled.map((row) => rowTimeError(row, timezone)).find(Boolean);
    if (timeError) return timeError;
    if (!isEdit) {
      if (!rows.length) return topCandidates ? "This job has no candidates to schedule." : "Select at least one candidate.";
      if (!jobdescId || (!fixedCandidate && !selectedJdUuid)) return "Select a job.";
      if (!selectedRound && !pendingForJob) return "Select a round.";
      if (!fixedCandidate && !topCandidates && !selectedCandidateIds.length) return "Select at least one candidate.";
    }
    if (!scheduled.length || scheduled.some((row) => !/^\d{4}-\d{2}-\d{2}$/.test(row.date || "") || !row.startTime || !row.endTime)) {
      return "Each candidate needs a date and a start time.";
    }
    if (showPicker && calendarProvider !== INTEGRATION_PROVIDER.GOOGLE && calendarProvider !== INTEGRATION_PROVIDER.MICROSOFT) {
      return "Select a meet type.";
    }
    return "";
  };

  const applyCalendarFailure = (described) => {
    if (described.hideMicrosoft) setHideMicrosoft(true);
    if (described.hideGoogle) setHideGoogle(true);
    setOfferAdminConsent(Boolean(described.offerAdminConsent));
    setConnectPrompt(described.connectProviders || []);
    setFormError(described.message);
  };

  const handleSave = async () => {
    setFormError("");
    if (nothingToSchedule) return;
    // Not enough AI credits for even one interview: the button works, and says how to get more
    if (creditsBlocked) {
      announcePlanBlocked(
        credits.inactive
          ? { code: "NO_ACTIVE_PLAN" }
          : {
              code: "PLAN_LIMIT_REACHED",
              metric: "ai_credits",
              message: "You don't have enough AI credits for this interview. Upgrade your plan or add credits to continue.",
            }
      );
      return;
    }
    setInterviewerError("");
    if (needsPanel && !panelistIds.length) {
      setPanelistFieldError(PANELIST_REQUIRED);
      return;
    }
    // The button stays enabled while the form loads, so a click before then waits for the data
    if (loadingOptions || loadingCandidates || ai.loading) {
      setFormError("Still loading. Try again in a moment.");
      return;
    }
    const message = formValidationError();
    if (message) {
      setFormError(message);
      return;
    }
    if (isEdit && !scheduled[0]?.date) {
      setFormError("Enter a date, a start time, and an end time.");
      return;
    }
    if (!isEdit) {
      if (!rows.length) {
        setFormError(topCandidates ? "This job has no candidates to schedule." : "Select at least one candidate.");
        return;
      }
    }

    if (!isReschedule && connectionsLoading) {
      setFormError("Checking calendars...");
      return;
    }
    if (!isReschedule && !connections) {
      setFormError(connectionsError || "Could not check calendar connections.");
      return;
    }
    if (bookingBlocked) {
      setFormError("Connect a calendar before scheduling.");
      return;
    }
    if (ai.selectionError) {
      setFormError(ai.selectionError);
      return;
    }

    const calendarBody = isReschedule || !showPicker
      ? {}
      : { calendar_provider: calendarProvider };
    // SiftHire saves the AI mode first; the AI Interviewer service is turned on or off after it succeeds
    const panelBody = { panelist_ids: panelistIds, ai_mode: ai.aiMode };

    setSaving(true);
    try {
      if (isEdit) {
        const body = {
          ...slotFor(scheduled[0]),
          ...panelBody,
          timezone: timezone.trim(),
          ...calendarBody,
        };
        if (aiOnlyChange) {
          if (ai.modeChanged) await setInterviewAIMode(interview.interview_id, ai.aiMode);
        } else if (isReschedule) {
          await api.post(`${BASE_URL}/api/interviews/${interview.interview_id}/reschedule`, {
            ...slotFor(scheduled[0]),
            ...panelBody,
            timezone: timezone.trim(),
            reason: rescheduleReason.trim(),
          });
        } else {
          await api.post(`${BASE_URL}/api/interviews/${interview.interview_id}/schedule`, body);
        }
        const aiApplied = await ai.apply([{ interviewId: interview.interview_id, label: "" }]);
        if (aiApplied) finishSave();
        return;
      }

      // Saved interviews in the shape the candidate's page lists them
      const asSaved = (items) => items
        .filter((item) => item?.interview_id)
        .map((item) => {
          const saved = normalizeInterview(item);
          return { ...saved, jdId: saved.jdId || jobdescId };
        });

      if (pendingForJob) {
        const response = await api.post(`${BASE_URL}/api/interviews/${pendingForJob.id}/schedule`, {
          ...slotFor(scheduled[0]),
          ...panelBody,
          timezone: timezone.trim(),
          ...calendarBody,
        });
        const booked = response.data?.interview || response.data || {};
        const saved = asSaved([{ ...booked, interview_id: booked.interview_id || pendingForJob.id }]);
        setSavedInterviews(saved);
        const aiApplied = await ai.apply([{ interviewId: pendingForJob.id, label: "" }]);
        if (aiApplied) finishSave(saved);
        return;
      }

      const slots = scheduled.map((row) => ({ candidate_id: row.candidate_id, ...slotFor(row) }));
      const response = await api.post(`${BASE_URL}/api/interviews`, {
        jd_id: jobdescId,
        round_id: selectedRound.round_id,
        candidate_ids: scheduled.map((row) => row.candidate_id),
        ...panelBody,
        timezone: timezone.trim(),
        ...calendarBody,
        slots,
      });
      const created = Array.isArray(response.data?.interviews) ? response.data.interviews : [];
      const saved = asSaved(created);
      setSavedInterviews(saved);
      const aiApplied = await ai.apply(
        created
          .filter((item) => item.interview_id)
          .map((item) => ({
            interviewId: item.interview_id,
            label: item.candidate?.name || item.candidate?.email || "",
          }))
      );
      const createErrors = Array.isArray(response.data?.errors) ? response.data.errors : [];
      if (createErrors.length) {
        const message = createErrors
          .map((item) => item.message || item.error || item.code || "A candidate could not be scheduled.")
          .join(" ");
        setFormError(message);
        if (created.length) onScheduled(saved);
        return;
      }
      if (aiApplied) finishSave(saved);
    } catch (err) {
      console.error("Error scheduling interview:", err);
      const field = validationErrorField(err);
      if (field === "panelist_ids") {
        setPanelistFieldError(PANELIST_REQUIRED);
      } else if (field === "ai_mode") {
        setInterviewerError(apiMessage(err, "Choose an interviewer."));
      } else if (err?.response?.status === 409 || err?.response?.data?.code) {
        applyCalendarFailure(describeCalendarError(
          err,
          isReschedule ? "Could not reschedule the interview." : isEdit ? "Could not schedule the interview." : "Could not create the interviews."
        ));
      } else {
        setFormError(apiMessage(err, isReschedule ? "Could not reschedule the interview." : isEdit ? "Could not schedule the interview." : "Could not create the interviews."));
      }
    } finally {
      setSaving(false);
      try {
        refreshPlan().catch(() => {});
      } catch {
        // The next open or focus refreshes it
      }
    }
  };

  const inputClass = "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white";
  const inputStyle = {
    borderColor: theme.palette.secondary.v4,
    color: "#111111",
    backgroundColor: "#ffffff",
  };
  const jobLabel = (job) => (job.title || job.ats_title || "").trim() || "Untitled job";
  // One known candidate (candidate's page or edit form) goes in the title; the list below shows the email
  const knownCandidate = isEdit ? interview.candidate : fixedCandidate;
  const candidateName = (knownCandidate?.name || "").trim();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70">
      <ErrorToast message={error} show={Boolean(error)} />
      <div className="w-full flex justify-center items-start mt-[70px] px-4">
        <div
          ref={modalRef}
          className="modal-content relative w-full max-w-47rem bg-white rounded-xl shadow-2xl max-h-[80vh] flex flex-col overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-interview-title"
        >
          <div
            className="modal-header flex justify-between items-center py-5 px-6 border-b"
            style={{
              backgroundColor: theme.palette.primary.main,
              borderColor: theme.palette.secondary.v4,
            }}
          >
            <h2 id="schedule-interview-title" className="text-xl font-semibold">
              {isReschedule ? "Reschedule interview" : "Schedule interview"}
              {candidateName ? ` with ${candidateName}` : ""}
            </h2>
            <button
              onClick={handleClose}
              aria-label="Close modal"
              className="inline-flex items-center justify-center rounded-full bg-black text-white h-8 w-8 hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-col px-5 sm:px-6 py-6 gap-5 flex-grow overflow-y-auto">
        <div className="contents">
        {isEdit && (interview.jd_title || interview.round_name || interview.status) && (
          <p className="text-sm" style={{ color: theme.palette.text.secondary }}>
            {[interview.jd_title, interview.round_name, interview.status].filter(Boolean).join(" · ")}
          </p>
        )}

        <AIInterviewerChoice
          label="Interview type"
          value={ai.choice}
          onChange={handleInterviewerChange}
          options={ai.options}
          disabled={saving || awaitingAIRetry}
          loading={ai.loading}
          error={ai.loadError}
          selectionError={interviewerError}
          failures={ai.failures}
          onRetry={handleRetryAI}
          retrying={ai.retrying}
        />
        {connectPromptText && (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <span>{connectPromptText}</span>
            <Button
              onClick={() => setConnectOpen(true)}
              sx={{ backgroundColor: theme.palette.info.main, color: theme.palette.text.light, textTransform: "none", px: 2, "&:hover": { backgroundColor: "#1D4690" } }}
            >
              {needsReconnect ? "Reconnect" : "Connect now"}
            </Button>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {showPicker && (
          <div className="sm:col-span-2">
            <p id="schedule-meet-type-label" className="block text-sm font-medium mb-2">
              Meet type <span className="text-red-500">*</span>
            </p>
            <MeetTypePicker
              labelledBy="schedule-meet-type-label"
              value={calendarProvider}
              onChange={setCalendarProvider}
              accounts={{
                google: connections?.google?.status === "connected" ? connections.google.accountEmail : "",
                microsoft: connections?.microsoft?.status === "connected" ? connections.microsoft.accountEmail : "",
              }}
              statuses={{
                google: connections?.google?.status || "",
                microsoft: connections?.microsoft?.status || "",
              }}
            />
          </div>
          )}
          {!isEdit && (
          <>
          <div>
            <label htmlFor="schedule-job" className="block text-sm font-medium mb-2">
              Job <span className="text-red-500">*</span>
            </label>
            {fixedCandidate ? (
              <FormDropdown
                id="schedule-job"
                searchable
                value={jobdescId}
                placeholder="Select job"
                onChange={setJobdescId}
                options={candidateJobs.map((job) => ({ value: String(job.id), label: job.title || "Untitled job" }))}
              />
            ) : (
              <FormDropdown
                id="schedule-job"
                searchable
                value={selectedJdUuid}
                placeholder="Select job"
                disabled={loadingOptions}
                onChange={(next) => {
                  setSelectedJdUuid(next);
                  setSelectedCandidateIds([]);
                }}
                options={jobs.filter((job) => job.jd_uuid).map((job) => ({
                  value: job.jd_uuid,
                  label: jobLabel(job),
                }))}
              />
            )}
          </div>
          <div>
            <label htmlFor="schedule-round" className="block text-sm font-medium mb-2">
              Round <span className="text-red-500">*</span>
            </label>
            <FormDropdown
              id="schedule-round"
              value={roundId}
              placeholder="Select round"
              disabled={loadingOptions || Boolean(pendingForJob)}
              onChange={(next) => {
                closeRoundForm();
                setRoundId(next);
              }}
              options={rounds.map((item) => ({
                value: String(item.round_id),
                label: item.name,
              }))}
              action={pendingForJob ? undefined : {
                label: "Add round",
                onClick: () => {
                  setEditingRoundId("");
                  setNewRoundName("");
                  setShowAddRound(true);
                  setRoundError("");
                },
              }}
            />
            {pendingForJob && (
              <p className="mt-1 text-xs" style={{ color: theme.palette.text.secondary }}>
                Books the pending interview for this job, so the round stays as it is.
              </p>
            )}
            {/* {selectedRound && !selectedRound.is_default && !showAddRound && (
              <div className="mt-2 flex items-center gap-2">
                <Button
                  onClick={() => {
                    setEditingRoundId(String(selectedRound.round_id));
                    setNewRoundName(selectedRound.name || "");
                    setShowAddRound(true);
                    setRoundError("");
                  }}
                  sx={{ color: theme.palette.info.main, textTransform: "none" }}
                >
                  Edit
                </Button>
                <Button
                  onClick={handleDeleteRound}
                  sx={{ color: "#b91c1c", textTransform: "none" }}
                >
                  Delete
                </Button>
              </div>
            )} */}
            {showAddRound && (
              <div className="mt-3 flex flex-col gap-2">
                <label htmlFor="new-round-name" className="block text-xs font-medium">
                  Round name <span className="text-red-500">*</span>
                </label>
                <input
                  id="new-round-name"
                  type="text"
                  value={newRoundName}
                  onChange={(e) => setNewRoundName(e.target.value)}
                  placeholder="Screening"
                  className={inputClass}
                  style={{ ...inputStyle, fontSize: "12px", padding: "6px 12px" }}
                />
                {roundError && <p className="text-sm text-red-600">{roundError}</p>}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSaveRound}
                    disabled={addingRound}
                    sx={{
                      backgroundColor: theme.palette.info.main,
                      color: theme.palette.text.light,
                      textTransform: "none",
                      fontSize: "12px",
                    }}
                  >
                    {addingRound ? "Saving..." : editingRoundId ? "Save" : "Add round"}
                  </Button>
                  <Button
                    onClick={closeRoundForm}
                    disabled={addingRound}
                    sx={{
                      color: theme.palette.text.main,
                      textTransform: "none",
                      fontSize: "12px",
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {roundError && !showAddRound && <p className="mt-2 text-sm text-red-600">{roundError}</p>}
          </div>
          </>
          )}
          <div className="sm:col-span-2">
            <label htmlFor="schedule-panelists" className="block text-sm font-medium mb-2">
              Panelists {needsPanel && <span className="text-red-500">*</span>}
            </label>
            <FormDropdown
              id="schedule-panelists"
              multiple
              searchable
              addLabel="Add panelist…"
              value={panelistIds}
              placeholder={loadingOptions ? "Loading..." : "Select panelists"}
              disabled={loadingOptions}
              onChange={(next) => {
                closePanelistForm();
                setPanelistIds(next);
                if (next.length) setPanelistFieldError("");
              }}
              options={interviewers.flatMap((person) => {
                const value = String(panelistIdOf(person) || "");
                if (!value) return [];
                const displayName = panelistDisplayName(person);
                return [{
                  value,
                  label: person.email ? `${displayName} (${person.email})` : displayName,
                  valueLabel: displayName || person.email,
                }];
              })}
              action={{
                label: "Add panelist",
                unavailable: panelistGate.blocked ? panelistGate.message : "",
                onClick: panelistGate.guard(() => {
                  setShowAddPanelist(true);
                  setNewPanelistFirstName("");
                  setNewPanelistLastName("");
                  setNewPanelistEmail("");
                  setPanelistError("");
                }),
              }}
            />
            {!needsPanel && (
              <p className="mt-1 text-xs" style={{ color: theme.palette.text.secondary }}>{PANELIST_OPTIONAL_HINT}</p>
            )}
            {panelistFieldError && <p className="mt-1 text-sm text-red-600" role="alert">{panelistFieldError}</p>}
            {showAddPanelist && (
              <div className="mt-3 flex flex-col gap-2">
                <div className="flex gap-2">
                  <div className="flex flex-col gap-2 flex-1">
                    <label htmlFor="new-panelist-first-name" className="block text-xs font-medium">
                      First name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="new-panelist-first-name"
                      type="text"
                      value={newPanelistFirstName}
                      onChange={(e) => {
                        setNewPanelistFirstName(e.target.value);
                        setPanelistError("");
                      }}
                      maxLength={200}
                      placeholder="Enter first name"
                      autoFocus
                      className={inputClass}
                      style={{ ...inputStyle, fontSize: "12px", padding: "6px 12px" }}
                    />
                  </div>
                  <div className="flex flex-col gap-2 flex-1">
                    <label htmlFor="new-panelist-last-name" className="block text-xs font-medium">
                      Last name
                    </label>
                    <input
                      id="new-panelist-last-name"
                      type="text"
                      value={newPanelistLastName}
                      onChange={(e) => {
                        setNewPanelistLastName(e.target.value);
                        setPanelistError("");
                      }}
                      maxLength={200}
                      placeholder="Enter last name"
                      className={inputClass}
                      style={{ ...inputStyle, fontSize: "12px", padding: "6px 12px" }}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="new-panelist-email" className="block text-xs font-medium">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="new-panelist-email"
                    type="email"
                    value={newPanelistEmail}
                    onChange={(e) => {
                      setNewPanelistEmail(e.target.value);
                      setPanelistError("");
                    }}
                    placeholder="Enter email address"
                    className={inputClass}
                    style={{ ...inputStyle, fontSize: "12px", padding: "6px 12px" }}
                  />
                </div>
                <p className="text-xs text-accent-gray-light">
                  They'll get an email invite to join as a Panelist. The link lasts 7 days.
                </p>
                {panelistError && <p className="text-sm text-red-600">{panelistError}</p>}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleCreatePanelist}
                    disabled={addingPanelist || !newPanelistFirstName.trim() || !newPanelistEmail.trim()}
                    sx={{
                      backgroundColor: theme.palette.info.main,
                      color: theme.palette.text.light,
                      textTransform: "none",
                      fontSize: "12px",
                    }}
                  >
                    {addingPanelist ? "Saving..." : "Add panelist"}
                  </Button>
                  <Button
                    onClick={closePanelistForm}
                    disabled={addingPanelist}
                    sx={{ color: theme.palette.text.main, textTransform: "none", fontSize: "12px" }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
          {!isEdit && !fixedCandidate && (
          <>
          <div>
            <p id="candidate-selection-label" className="block text-sm font-medium mb-2">
              Candidates
            </p>
            <div
              role="radiogroup"
              aria-labelledby="candidate-selection-label"
              className="grid grid-cols-2 gap-1 rounded-md border p-1"
              style={{ borderColor: theme.palette.secondary.v4 }}
            >
              {[
                { value: true, label: "Top-ranked" },
                { value: false, label: "Pick candidates" },
              ].map((option) => {
                const selected = topCandidates === option.value;
                return (
                  <button
                    key={option.label}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => {
                      if (selected) return;
                      setTopCandidates(option.value);
                      setSelectedCandidateIds([]);
                    }}
                    className="rounded px-3 py-1 text-sm font-medium"
                    style={selected
                      ? { backgroundColor: theme.palette.info.main, color: theme.palette.text.light }
                      : { color: theme.palette.text.secondary }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            {topCandidates ? (
              <>
                <label htmlFor="schedule-count" className="block text-sm font-medium mb-2">
                  Number of candidates <span className="text-red-500">*</span>
                </label>
                <input
                  id="schedule-count"
                  type="number"
                  min="1"
                  value={candidateCount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setCandidateCount("");
                      return;
                    }
                    const parsed = parseInt(value, 10);
                    if (Number.isNaN(parsed)) return;
                    setCandidateCount(String(Math.max(1, parsed)));
                  }}
                  className={inputClass}
                  style={inputStyle}
                />
              </>
            ) : (
              <>
                <label htmlFor="schedule-candidates" className="block text-sm font-medium mb-2">
                  Select candidates <span className="text-red-500">*</span>
                </label>
                <FormDropdown
                  id="schedule-candidates"
                  multiple
                  searchable
                  value={selectedCandidateIds}
                  placeholder={selectedJdUuid ? (loadingCandidates ? "Loading candidates" : "Select candidates") : "Select a job first"}
                  disabled={!selectedJdUuid || loadingCandidates || schedulablePool.length === 0}
                  onChange={setSelectedCandidateIds}
                  options={schedulablePool.map((candidate) => {
                    const name = displayName(candidate);
                    const email = (candidate.email || "").trim();
                    return {
                      value: candidateIdOf(candidate),
                      label: email ? `${name} (${email})` : name,
                      valueLabel: name,
                    };
                  })}
                />
              </>
            )}
          </div>
          </>
          )}
        </div>
        {isReschedule && (
            <div>
              <label htmlFor="reschedule-reason" className="block text-sm font-medium mb-2">
                Reason
              </label>
              <input
                id="reschedule-reason"
                type="text"
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                placeholder="Why this time is changing"
                className={inputClass}
                style={inputStyle}
              />
            </div>
        )}


        <section aria-labelledby="schedule-when-label" className="flex flex-col gap-3">
          <p id="schedule-when-label" className="text-sm font-medium">Date and time</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {!timeEach && (
              <>
                <div>
                  <label htmlFor="schedule-date" className="block text-xs font-medium mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="schedule-date"
                    type="date"
                    value={slotDate}
                    min={earliestBookableDate(timezone)}
                    onChange={(e) => setSlotDate(e.target.value)}
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label htmlFor="schedule-start" className="block text-xs font-medium mb-1">
                    Start time <span className="text-red-500">*</span>
                  </label>
                  <FormDropdown
                    id="schedule-start"
                    value={slotTime}
                    placeholder="Start time"
                    onChange={setSlotTime}
                    options={startTimeChoices(slotDate, timezone, slotTime)}
                  />
                </div>
              </>
            )}
            <div>
              <label htmlFor="schedule-duration" className="block text-xs font-medium mb-1">
                Duration <span className="text-red-500">*</span>
              </label>
              <FormDropdown
                id="schedule-duration"
                value={duration}
                onChange={setDuration}
                options={durationChoices(duration)}
              />
            </div>
            <div>
              <label htmlFor="schedule-timezone" className="block text-xs font-medium mb-1">
                Timezone <span className="text-red-500">*</span>
              </label>
              <FormDropdown
                id="schedule-timezone"
                searchable
                value={timezone}
                placeholder="Select timezone"
                onChange={setTimezone}
                options={timezoneChoices(timezone)}
              />
            </div>
          </div>

          <AICreditCost kind={creditKind} summary={credits} minutes={duration} count={creditCount} durationText={durationLabel(duration)} />

          {multiple && (
            <div className="flex flex-col gap-1">
              {!timeEach && (
                <p className="text-sm" style={{ color: theme.palette.text.secondary }}>
                  {parallel
                    ? "Everyone is interviewed at the same time. The AI runs each interview separately."
                    : "Candidates are scheduled back to back, so the panel isn't double-booked."}
                </p>
              )}
              <label className="flex items-center gap-2 text-sm cursor-pointer w-fit">
                <input type="checkbox" checked={timeEach} onChange={toggleTimeEach} />
                Set a different time for each candidate
              </label>
            </div>
          )}

          {(isEdit || fixedCandidate || selectedJdUuid) && (
            loadingCandidates ? (
              <p className="text-sm">Loading candidates...</p>
            ) : scheduled.length === 0 ? (
              <p className="text-sm">
                {!topCandidates && schedulablePool.length
                  ? "Select candidates to schedule."
                  : pool.length
                    ? "None of these candidates can be scheduled until an email is added."
                    : "No candidates found for this job."}
              </p>
            ) : (
              <ul className="flex flex-col divide-y rounded-lg border" style={{ borderColor: theme.palette.secondary.v4 }}>
                {scheduled.map((row, index) => {
                  const rowError = rowTimeError(row, timezone);
                  return (
                    <li key={row.candidate_id} className="flex flex-col gap-2 px-4 py-3">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{row.name || "Candidate"}</p>
                          <p className="text-xs truncate" style={{ color: theme.palette.text.secondary }}>
                            {row.email || "No email"}
                          </p>
                        </div>
                        {timeEach ? (
                          <div className="grid grid-cols-2 gap-2 w-full sm:w-80">
                            <input
                              type="date"
                              aria-label={`Date for ${row.name || "candidate"}`}
                              value={row.date}
                              min={earliestBookableDate(timezone)}
                              onChange={(e) => updateRow(index, { date: e.target.value })}
                              className={inputClass}
                              style={inputStyle}
                            />
                            <FormDropdown
                              id={`start-${index}`}
                              value={row.startTime}
                              placeholder="Start time"
                              onChange={(next) => updateRow(index, { startTime: next })}
                              options={startTimeChoices(row.date, timezone, row.startTime)}
                            />
                          </div>
                        ) : (
                          <p className="text-sm whitespace-nowrap">
                            {slotLabel(row)}
                          </p>
                        )}
                      </div>
                      {rowError && <p className="text-sm text-red-600">{rowError}</p>}
                    </li>
                  );
                })}
              </ul>
            )
          )}

          {/* One error at a time: the slot's own error explains why the AI can't run in it too */}
          {scheduled.length > 0 && ai.selectionError && !scheduled.some((row) => rowTimeError(row, timezone)) && (
            <p className="text-sm text-red-600" role="alert">{ai.selectionError}</p>
          )}
        </section>

        {!loadingCandidates && fewerNote && <p className="text-sm">{fewerNote}</p>}
        {!loadingCandidates && unschedulableNote && <p className="text-sm">{unschedulableNote}</p>}
        </div>
        {formError && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {formError}
            {offerAdminConsent && <AdminConsentOffer />}
            {connectPrompt.length > 0 && (
              <div className="mt-2">
                <ProviderConnectButtons providerIds={connectPrompt} connections={connections} />
              </div>
            )}
          </div>
        )}
          </div>

          <ConnectCalendarDialog
            open={connectOpen}
            onClose={() => {
              setConnectOpen(false);
              // Whatever was connected in there is what can be booked now
              if (refreshConnections) refreshConnections();
            }}
            onChanged={refreshConnections}
          />

          <div
            className="w-full py-4 px-6 flex justify-end gap-3 border-t"
            style={{ borderColor: theme.palette.secondary.v4 }}
          >
            
            {awaitingAIRetry ? (
              // Retry sits next to the Interviewer field; closing keeps the interview without AI
              <Button
                onClick={handleClose}
                disabled={ai.retrying}
                sx={{ color: theme.palette.text.main, textTransform: "none" }}
              >
                Close
              </Button>
            ) : (
            <>
            <Button
              onClick={handleClose}
              disabled={saving}
              variant="outlined"
              sx={{
                color: theme.palette.text.main,
                borderColor: theme.palette.secondary.v4,
                backgroundColor: "#ffffff",
                textTransform: "none",
                px: 2.5,
                "&:hover": { backgroundColor: "#f7f7f7", borderColor: theme.palette.secondary.main },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || loadingCandidates || nothingToSchedule || bookingBlocked || ai.loading}
              sx={{
                backgroundColor: theme.palette.info.main,
                color: theme.palette.text.light,
                textTransform: "none",
                px: 2.5,
              }}
            >
              {saving
                ? "Saving..."
                : aiOnlyChange
                ? "Save"
                : isReschedule
                ? "Reschedule"
                : isEdit
                ? "Schedule"
                : "Create interview"}
            </Button>
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleInterviewModal;
